import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import toast from "react-hot-toast";
import ProfileMenu from "../components/ProfileMenu";
import {
  Phone, Satellite, BarChart3, Lock, ShieldCheck, Activity, ScrollText,
  Shuffle, Music, MonitorCog, Shield, Settings as SettingsIcon, BookOpen,
  Globe, Plug, Zap, Check, X, AlertTriangle, Info, Pin, Play, Loader,
  Copy, Save, Download, Printer, Terminal, Clipboard, FileJson, Key,
  KeyRound, UserCheck, Lightbulb, CircleHelp, Target, MapPin, Server,
  Network, FlaskConical, Bell, Wrench, ListChecks, Hammer, FileText,
} from "lucide-react";

import { clearAllTokens } from "../utils/tokenStorage";
const TEST_LABELS={dns:"DNS Resolution",tcp:"TCP Connect",udp_sip:"SIP OPTIONS",tls:"TLS Certificate",icmp:"ICMP Ping",srv:"DNS SRV Records",quality:"Call Quality (MOS)",register:"SIP REGISTER",invite:"SIP INVITE"};
const TEST_ICONS={dns:Globe,tcp:Plug,udp_sip:Phone,tls:Lock,icmp:Satellite,srv:Network,quality:Activity,register:KeyRound,invite:Phone};
const mosColor=m=>m>=4.0?"var(--au-success)":m>=3.6?"var(--au-warn)":"var(--au-error)";
const SERVER_TYPES=[
  {name:"Generic SIP",port:5060,note:"Standard SIP UDP/TCP"},
  {name:"Asterisk (chan_sip)",port:5060,note:"Default Asterisk channel"},
  {name:"Asterisk (PJSIP)",port:5060,note:"Modern Asterisk (may also use 5080)"},
  {name:"FreeSWITCH Internal",port:5060,note:"Internal profile — registered users"},
  {name:"FreeSWITCH External",port:5080,note:"External profile — public-facing trunks"},
  {name:"Kamailio Proxy",port:5060,note:"SIP proxy / B2BUA"},
  {name:"OpenSIPS",port:5060,note:"Carrier-grade SIP proxy"},
  {name:"3CX PBX",port:5060,note:"3CX commercial PBX"},
  {name:"SIP over TLS",port:5061,note:"Encrypted signaling"},
  {name:"SIP WebSocket (WSS)",port:443,note:"For WebRTC clients"}
];
const sleep=ms=>new Promise(r=>setTimeout(r,ms));

// Small helper for rendering a lucide icon by component reference
function Ico({icon:I,size=16,style}){return I?<I size={size} style={style}/>:null;}

export default function SipTester() {
  const [activeTab,setActiveTab]=useState("test");
  const [server,setServer]=useState("");
  const [port,setPort]=useState("5060");
  const [serverType,setServerType]=useState("Generic SIP");
  const [enabled,setEnabled]=useState({dns:true,tcp:true,udp_sip:true,tls:true,icmp:true,srv:false,quality:false,register:false,invite:false});
  const [creds,setCreds]=useState({username:"",password:"",to_user:"echo"});
  const [busy,setBusy]=useState(false);
  const [result,setResult]=useState(null);
  const [liveLog,setLiveLog]=useState([]);
  const [history,setHistory]=useState([]);
  const [stats,setStats]=useState({total_tests:0,tests_this_month:0,success_rate:null,avg_latency_ms:null});
  const [user,setUser]=useState(null);
  const [dialplan,setDialplan]=useState("");
  const [dialplanResult,setDialplanResult]=useState(null);
  const [savedServers,setSavedServers]=useState(()=>{try{return JSON.parse(localStorage.getItem("aegis_sip_servers"))||[];}catch{return[];}});
  const [settings,setSettings]=useState(()=>{try{return JSON.parse(localStorage.getItem("aegis_sip_settings"))||{defaultPort:"5060",timeoutSec:5,autoSaveResults:true,emailAlerts:false,emailOnFail:false};}catch{return{defaultPort:"5060",timeoutSec:5,autoSaveResults:true,emailAlerts:false,emailOnFail:false};}});
  const nav=useNavigate();

  useEffect(()=>{api.get("/api/auth/me").then(r=>setUser(r.data)).catch(()=>nav("/login"));loadHistory();loadStats();},[]);
  useEffect(()=>{localStorage.setItem("aegis_sip_settings",JSON.stringify(settings));},[settings]);
  useEffect(()=>{localStorage.setItem("aegis_sip_servers",JSON.stringify(savedServers));},[savedServers]);

  const loadHistory=async()=>{try{const r=await api.get("/api/sip-test/history");setHistory(r.data.tests||[]);}catch(e){}};
  const loadStats=async()=>{try{const r=await api.get("/api/sip-test/stats");setStats(r.data);}catch(e){}};

  const runTest=async()=>{
    if(!server.trim()){toast.error("SIP server required");return;}
    const tests=Object.keys(enabled).filter(k=>enabled[k]);
    if(tests.length===0){toast.error("Select at least one test");return;}
    setBusy(true);setResult(null);
    const log=[];
    const add=(level,msg)=>{const t=new Date().toTimeString().slice(0,8);log.push({t,level,msg});setLiveLog([...log]);};

    add("info","═══════════════════════════════════════");
    add("info",`GetKhojo SIP Diagnostic — ${server}:${port}`);
    add("muted",`Server type: ${serverType}`);
    add("info","═══════════════════════════════════════");
    await sleep(250);
    for(const k of tests){add("running",`→ ${TEST_LABELS[k]}...`);await sleep(150);}
    add("info","");
    add("muted","Sending probes to server...");
    await sleep(300);

    try{
      const res=await api.post("/api/sip-test/run",{server:server.trim(),port:parseInt(port)||5060,tests,username:creds.username||null,password:creds.password||null,to_user:creds.to_user||"echo"});
      const data=res.data;
      add("info","");
      for(const[k,t]of Object.entries(data.tests||{})){
        await sleep(280);
        const sym=t.status==="pass"?"✓":t.status==="no_response"?"⚠":"✗";
        const col=t.status==="pass"?"success":t.status==="no_response"?"warn":"error";
        add(col,`${sym} ${TEST_LABELS[k]} → ${t.status.toUpperCase()}`);
        if(t.ip)add("muted",`     IP: ${t.ip}`);
        if(t.latency_ms!=null)add("muted",`     Latency: ${t.latency_ms} ms`);
        if(t.avg_ms!=null)add("muted",`     Avg ping: ${t.avg_ms} ms`);
        if(t.time_ms!=null)add("muted",`     DNS time: ${t.time_ms} ms`);
        if(t.cert_valid!=null)add("muted",`     Cert: ${t.cert_valid?"valid":"invalid"}`);
        if(t.expiry)add("muted",`     Expires: ${new Date(t.expiry).toLocaleDateString()}`);
        if(t.issuer)add("muted",`     Issuer: ${t.issuer.slice(0,60)}`);
        if(t.response){
          add("muted","     Response:");
          t.response.split("\n").slice(0,3).forEach(line=>add("muted",`       ${line.trim()}`));
        }
        if(t.error)add("error",`     ✗ ${t.error}`);
        if(t.note)add("warn",`     ℹ ${t.note}`);
      }
      await sleep(300);
      add("info","");
      add("info","═══════════════════════════════════════");
      const sc=data.overall_score;
      const sCol=sc>=70?"success":sc>=40?"warn":"error";
      add(sCol,`Overall Score: ${sc}/100`);
      add("success","✓ Report saved to history");
      add("info","═══════════════════════════════════════");
      setResult(data);loadHistory();loadStats();
      toast.success(`Score: ${sc}/100`);
    }catch(err){
      add("error",`✗ Test failed: ${err.message||err.response?.data?.detail||"Unknown"}`);
      toast.error("Test failed");
    }finally{setBusy(false);}
  };

  const onServerTypeChange=(name)=>{setServerType(name);const t=SERVER_TYPES.find(x=>x.name===name);if(t)setPort(t.port.toString());};
  const saveCurrentServer=()=>{if(!server.trim())return;const entry={server:server.trim(),port,type:serverType,saved:Date.now()};setSavedServers([entry,...savedServers.filter(s=>s.server!==entry.server)].slice(0,10));toast.success("Server saved");};
  const useSavedServer=(s)=>{setServer(s.server);setPort(s.port);setServerType(s.type||"Generic SIP");};

  const downloadJSON=()=>{if(!result)return;const blob=new Blob([JSON.stringify(result,null,2)],{type:"application/json"});const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`sip-test-${result.server}-${Date.now()}.json`;a.click();URL.revokeObjectURL(url);toast.success("JSON downloaded");};
  const copyResult=()=>{if(!result)return;navigator.clipboard?.writeText(JSON.stringify(result,null,2));toast.success("Result copied");};
  const printReport=()=>{window.print();};
  const downloadLog=()=>{const text=liveLog.map(l=>`[${l.t}] ${l.msg}`).join("\n");const blob=new Blob([text],{type:"text/plain"});const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`sip-log-${Date.now()}.txt`;a.click();URL.revokeObjectURL(url);toast.success("Log saved");};
  const copyLog=()=>{const text=liveLog.map(l=>`[${l.t}] ${l.msg}`).join("\n");navigator.clipboard?.writeText(text);toast.success("Log copied");};

  const viewHistoryTest=async(id)=>{try{const r=await api.get(`/api/sip-test/test/${id}`);setResult(r.data);setActiveTab("test");window.scrollTo({top:400,behavior:"smooth"});}catch(e){toast.error("Failed");}};

  const analyzeDialplan=()=>{
    if(!dialplan.trim()){toast.error("Paste dialplan first");return;}
    const extensions=(dialplan.match(/exten\s*=>\s*[^,\s]+/g)||[]).map(s=>s.split("=>")[1].trim());
    const contexts=(dialplan.match(/\[[\w-]+\]/g)||[]).map(s=>s.slice(1,-1));
    const actions=(dialplan.match(/(?:application=|same\s*=>\s*n,)\s*\w+/gi)||[]).map(s=>s.replace(/.*[=,]\s*/,""));
    const fsExt=(dialplan.match(/<extension\s+name="([^"]+)"/g)||[]).map(s=>s.match(/name="([^"]+)"/)[1]);
    const krRoutes=(dialplan.match(/route\[([^\]]+)\]/g)||[]).map(s=>s.match(/route\[([^\]]+)\]/)[1]);
    const allExt=[...extensions,...fsExt];
    const type=krRoutes.length>0?"Kamailio":fsExt.length>0?"FreeSWITCH":extensions.length>0?"Asterisk":"Unknown";
    setDialplanResult({type,extensions:[...new Set(allExt)],contexts:[...new Set(contexts)],routes:krRoutes,actions:[...new Set(actions)].slice(0,20),lines:dialplan.split("\n").length});
    toast.success(`Parsed: ${type} dialplan`);
  };

  const logout=()=>{clearAllTokens();nav("/login");};
  const TabT={test:"SIP Server Diagnostics",history:"Test History","multi-auth":"Multi-Auth Test",dialplan:"Dialplan Analyzer",flow:"SIP Flow Reference",codecs:"VoIP Codecs",api:"API & CLI Access",forensics:"Call Forensics",settings:"Settings",docs:"How to Use"};
  const TabS={test:"Live network diagnostics — DNS, TCP, UDP-SIP, TLS, ICMP",history:`${history.length} tests stored`,"multi-auth":"IP / Digest / OAuth / API Key — full coverage",dialplan:"Parse Asterisk, FreeSWITCH, Kamailio configs",flow:"Standard SIP call flow reference",codecs:"Bandwidth, quality, compatibility",api:"Run diagnostics from your terminal",forensics:"Trace calls on compromised servers",settings:"Saved servers, defaults, alerts",docs:"Guide — which IP, ports, auth methods"};

  return (
    <div className="au-root" data-theme="sip" style={{display:"flex"}}>
      <aside style={S.sidebar} className="au-glass no-print">
        <div style={S.brand}>
          <div style={{display:"flex",alignItems:"center",gap:8,fontSize:22,fontWeight:800,color:"var(--au-accent-ink)",letterSpacing:1}}><Phone size={22}/> SIP Tester</div>
          <div style={{fontSize:10,color:"var(--au-muted)",letterSpacing:2,marginTop:4}}>BY GETKHOJO</div>
        </div>
        <nav style={{flex:1,padding:"16px 12px",overflowY:"auto"}}>
          <NavItem icon={Satellite} label="SIP Test" active={activeTab==="test"} onClick={()=>setActiveTab("test")}/>
          <NavItem icon={BarChart3} label="Test History" active={activeTab==="history"} onClick={()=>setActiveTab("history")} badge={history.length}/>
          <NavItem icon={ShieldCheck} label="Multi-Auth Test" active={activeTab==="multi-auth"} onClick={()=>setActiveTab("multi-auth")}/>
          <NavItem icon={ScrollText} label="Dialplan" active={activeTab==="dialplan"} onClick={()=>setActiveTab("dialplan")}/>
          <NavItem icon={Shuffle} label="SIP Flow" active={activeTab==="flow"} onClick={()=>setActiveTab("flow")}/>
          <NavItem icon={Music} label="Codecs" active={activeTab==="codecs"} onClick={()=>setActiveTab("codecs")}/>
          <NavItem icon={MonitorCog} label="API & CLI" active={activeTab==="api"} onClick={()=>setActiveTab("api")} paid/>
          <NavItem icon={Shield} label="Call Forensics" active={activeTab==="forensics"} onClick={()=>setActiveTab("forensics")} paid/>
          <NavItem icon={SettingsIcon} label="Settings" active={activeTab==="settings"} onClick={()=>setActiveTab("settings")}/>
          <NavItem icon={BookOpen} label="How to Use" active={activeTab==="docs"} onClick={()=>setActiveTab("docs")}/>
        </nav>
        <div style={S.userBox}>
          <div style={{color:"var(--au-text)",fontWeight:600,marginBottom:4,fontSize:13}}>{user?.name||"User"}</div>
          <div style={{color:"var(--au-muted)",fontSize:11,marginBottom:10,wordBreak:"break-all"}}>{user?.email}</div>
          <button onClick={logout} className="au-pill au-pill-glass au-pill-sm" style={{width:"100%"}}>Sign Out</button>
        </div>
      </aside>

      <main style={{flex:1,padding:"32px 44px",overflowY:"auto"}}>
        <div style={S.headerRow}>
          <div>
            <h1 style={{fontSize:28,fontWeight:700,color:"var(--au-text)",margin:0}}>{TabT[activeTab]}</h1>
            <p style={{color:"var(--au-text-2)",fontSize:14,marginTop:4}}>{TabS[activeTab]}</p>
          </div>
          <div style={{display:"flex",gap:14,alignItems:"center"}}>
            <span className="au-chip au-chip-status"><span className="au-led"/> BETA — by GetKhojo</span>
            <ProfileMenu />
          </div>
        </div>

        {(activeTab==="test"||activeTab==="history")&&(
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:28}}>
            <StatCard icon={BarChart3} label="Tests Run" value={stats.total_tests||0} hint="All-time"/>
            <StatCard icon={Check} label="Success Rate" value={stats.success_rate!=null?`${stats.success_rate}%`:"—"} hint={`${stats.tests_this_month||0} this month`}/>
            <StatCard icon={Zap} label="Avg Latency" value={stats.avg_latency_ms!=null?`${stats.avg_latency_ms} ms`:"—"} hint="TCP across tests"/>
            <StatCard icon={Phone} label="Plan" value={(user?.plan||"FREE").toUpperCase()} hint="Upgrade for limits"/>
          </div>
        )}

        {activeTab==="test"&&<TestTab {...{server,setServer,port,setPort,serverType,onServerTypeChange,enabled,setEnabled,creds,setCreds,busy,runTest,result,setActiveTab,saveCurrentServer,savedServers,useSavedServer,liveLog,downloadLog,copyLog,downloadJSON,copyResult,printReport}}/>}
        {activeTab==="history"&&<HistoryTab {...{history,viewHistoryTest}}/>}
        {activeTab==="multi-auth"&&<MultiAuthTab/>}
        {activeTab==="dialplan"&&<DialplanTab {...{dialplan,setDialplan,analyzeDialplan,dialplanResult,setDialplanResult}}/>}
        {activeTab==="flow"&&<FlowTab/>}
        {activeTab==="codecs"&&<CodecsTab/>}
        {activeTab==="api"&&<APICliTab/>}
        {activeTab==="forensics"&&<ForensicsTab/>}
        {activeTab==="settings"&&<SettingsTab {...{settings,setSettings,enabled,setEnabled,savedServers,setSavedServers}}/>}
        {activeTab==="docs"&&<DocsTab/>}
      </main>
    </div>
  );
}

function TestTab(p) {
  const {server,setServer,port,setPort,serverType,onServerTypeChange,enabled,setEnabled,creds,setCreds,busy,runTest,result,setActiveTab,saveCurrentServer,savedServers,useSavedServer,liveLog,downloadLog,copyLog,downloadJSON,copyResult,printReport}=p;
  return (
    <>
      <div className="au-glass" style={S.card}>
        <div style={S.cardHead}><h2 style={S.cardH2}><Satellite size={18}/> New SIP Diagnostic</h2></div>
        <div style={{padding:"24px 28px"}}>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:18,marginBottom:18}}>
            <div>
              <label style={S.label}>SERVER TYPE</label>
              <select value={serverType} onChange={e=>onServerTypeChange(e.target.value)} className="au-input" style={{cursor:"pointer"}}>
                {SERVER_TYPES.map(t=><option key={t.name} value={t.name}>{t.name} (default :{t.port})</option>)}
              </select>
              <div style={S.hint}>{SERVER_TYPES.find(t=>t.name===serverType)?.note}</div>
            </div>
            <div>
              <label style={S.label}>PORT</label>
              <input value={port} onChange={e=>setPort(e.target.value.replace(/\D/g,""))} placeholder="5060" className="au-input"/>
              <div style={S.hint}>Override default port</div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"2fr auto",gap:10,alignItems:"end",marginBottom:18}}>
            <div>
              <label style={S.label}>SIP SERVER ADDRESS *</label>
              <input value={server} onChange={e=>setServer(e.target.value)} placeholder="your-pbx.example.com or 203.0.113.45" className="au-input"/>
              <div style={S.hint}>Hostname or IP of YOUR server. <a href="#" onClick={e=>{e.preventDefault();setActiveTab("docs");}} style={{color:"var(--au-accent-ink)",textDecoration:"none"}}>Which IP? See Docs.</a></div>
            </div>
            <button onClick={saveCurrentServer} disabled={!server.trim()} className="au-pill au-pill-glass">+ Save</button>
          </div>

          {savedServers.length>0&&(
            <div style={{marginBottom:18}}>
              <div style={S.miniLabel}>SAVED SERVERS</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {savedServers.map((s,i)=><button key={i} onClick={()=>useSavedServer(s)} className="au-chip au-chip-status" style={{cursor:"pointer"}}><Pin size={13}/> {s.server}:{s.port}</button>)}
              </div>
            </div>
          )}

          <div style={{marginBottom:20}}>
            <div style={S.miniLabel}>TESTS TO RUN</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:10}}>
              {Object.keys(TEST_LABELS).map(k=>(
                <label key={k} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",border:`1px solid ${enabled[k]?"var(--au-accent)":"var(--au-hair)"}`,borderRadius:"var(--au-r-input)",background:enabled[k]?"var(--au-glass-strong)":"var(--au-glass)",cursor:"pointer"}}>
                  <input type="checkbox" checked={enabled[k]} onChange={e=>setEnabled({...enabled,[k]:e.target.checked})} style={{accentColor:"var(--au-accent)",width:16,height:16}}/>
                  <span style={{display:"inline-flex",alignItems:"center",gap:8,fontSize:13,fontWeight:600,color:enabled[k]?"var(--au-accent-ink)":"var(--au-text-2)"}}><Ico icon={TEST_ICONS[k]} size={16}/> {TEST_LABELS[k]}</span>
                </label>
              ))}
            </div>
          </div>

          {(enabled.register||enabled.invite)&&(
            <div style={{marginBottom:20,padding:"16px 18px",border:"1px solid var(--au-hair)",borderRadius:"var(--au-r-input)",background:"var(--au-glass)"}}>
              <div style={{...S.miniLabel,marginBottom:10,display:"flex",alignItems:"center",gap:6}}><KeyRound size={13}/> SIP CREDENTIALS <span style={{fontWeight:400,textTransform:"none",color:"var(--au-muted)"}}>— for REGISTER / INVITE digest auth (optional; INVITE works unauthenticated too)</span></div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
                <div><label style={S.label}>USERNAME</label><input value={creds.username} onChange={e=>setCreds({...creds,username:e.target.value})} placeholder="100" className="au-input" autoComplete="off"/></div>
                <div><label style={S.label}>PASSWORD</label><input type="password" value={creds.password} onChange={e=>setCreds({...creds,password:e.target.value})} placeholder="••••••••" className="au-input" autoComplete="new-password"/></div>
                {enabled.invite&&<div><label style={S.label}>INVITE TO USER</label><input value={creds.to_user} onChange={e=>setCreds({...creds,to_user:e.target.value})} placeholder="echo" className="au-input"/></div>}
              </div>
              <div style={{...S.hint,marginTop:8}}>Credentials are sent once to run the test and never stored.</div>
            </div>
          )}

          <button onClick={runTest} disabled={busy} className="au-pill au-pill-primary" style={{width:"100%",justifyContent:"center",padding:16,fontSize:15,cursor:busy?"wait":"pointer",opacity:busy?0.85:1}}>{busy?<><Loader size={18}/> Running diagnostics...</>:<><Play size={18}/> Start SIP Test</>}</button>
        </div>
      </div>

      {liveLog.length>0&&<LiveTerminal log={liveLog} busy={busy} onCopy={copyLog} onDownload={downloadLog}/>}

      {result&&<ResultPanel result={result}/>}
      {result&&<ExportBanner result={result} onDownloadJSON={downloadJSON} onCopy={copyResult} onPrint={printReport}/>}

      <div style={{marginTop:28,display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
        <InfoCard icon={Globe} title="Reachability" desc="Verify SIP server reachable from public internet"/>
        <InfoCard icon={Lock} title="TLS Inspection" desc="Validate cert chain, expiry, issuer on TLS ports"/>
        <InfoCard icon={Zap} title="Latency" desc="Round-trip time + detect packet loss"/>
      </div>
    </>
  );
}

function LiveTerminal({log,busy,onCopy,onDownload}) {
  const ref=useRef(null);
  useEffect(()=>{if(ref.current)ref.current.scrollTop=ref.current.scrollHeight;},[log]);
  const colors={info:"#9CA3AF",running:"#60A5FA",success:"#34D419",error:"#F2503C",warn:"#F4BE50",muted:"#9CA3AF"};
  return (
    <div style={{marginTop:18}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,padding:"0 4px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,color:"var(--au-muted)",letterSpacing:1.5,fontWeight:700}}><Terminal size={14}/> LIVE TEST OUTPUT</span>
          {busy&&<span className="au-chip au-chip-status" style={{color:"var(--au-warn)"}}><span className="au-led" style={{background:"var(--au-warn)",boxShadow:"0 0 6px var(--au-warn)"}}/> RUNNING</span>}
        </div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={onCopy} className="au-pill au-pill-glass au-pill-sm"><Clipboard size={14}/> Copy</button>
          <button onClick={onDownload} className="au-pill au-pill-glass au-pill-sm"><Save size={14}/> Save .txt</button>
        </div>
      </div>
      <div ref={ref} className="au-mono" style={{background:"var(--au-bg-artboard, #0a0a0a)",fontFamily:"var(--au-font-mono)",padding:14,borderRadius:"var(--au-r-input)",maxHeight:340,overflowY:"auto",fontSize:12,lineHeight:1.7,border:"1px solid #1F2937"}}>
        {log.map((l,i)=>(
          <div key={i} style={{color:colors[l.level]||colors.info,whiteSpace:"pre",wordBreak:"break-all"}}>
            <span style={{color:"#6B7280"}}>[{l.t}]</span>{" "}{l.msg}
          </div>
        ))}
        {busy&&<div style={{color:"var(--au-success)",display:"inline-block",animation:"blink 1s steps(1) infinite"}}>▊</div>}
      </div>
            <style>{`
        @media print {
          aside, nav, .no-print { display: none !important; }
          main { padding: 0 !important; }
          body { background: white !important; }
        }
        @keyframes blink{50%{opacity:0}}
      `}</style>
    </div>
  );
}

function ResultPanel({result}) {
  const tests=result.tests||{};const sc=result.overall_score??0;
  const sCol=sc>=70?"var(--au-success)":sc>=40?"var(--au-warn)":"var(--au-error)";
  return (
    <div className="au-glass" style={{...S.card,marginTop:18}}>
      <div style={{...S.cardHead,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={S.cardH2}><BarChart3 size={18}/> Results — {result.server}:{result.port}</h2>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{fontSize:11,color:"var(--au-muted)",letterSpacing:2,fontWeight:600}}>SCORE</div>
            <div style={{fontSize:32,fontWeight:800,color:sCol,fontFamily:"var(--au-font-mono)"}}>{sc}<span style={{fontSize:14,color:"var(--au-muted)"}}>/100</span></div>
          </div>

        </div>
      </div>
      <div style={{padding:"20px 28px"}}>
        {Object.entries(tests).map(([k,t])=><TestResultCard key={k} testKey={k} test={t}/>)}
      </div>
    </div>
  );
}


function ExportBanner({result,onDownloadJSON,onCopy,onPrint}) {
  return (
    <div className="au-glass" style={{marginTop:18,padding:"22px 28px",borderColor:"var(--au-accent)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:14}}>
      <div>
        <div style={{display:"flex",alignItems:"center",gap:8,fontSize:17,fontWeight:800,color:"var(--au-text)",marginBottom:4}}><Download size={18}/> Export This Report</div>
        <div style={{fontSize:12,color:"var(--au-text-2)"}}>Save · Share · Archive — Score {result.overall_score}/100 for <strong style={{color:"var(--au-text)"}}>{result.server}:{result.port}</strong></div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <button onClick={onDownloadJSON} className="au-pill au-pill-primary au-pill-sm"><Save size={14}/> Download JSON</button>
        <button onClick={onCopy} className="au-pill au-pill-primary au-pill-sm"><Clipboard size={14}/> Copy Full Report</button>
        <button onClick={onPrint} className="au-pill au-pill-primary au-pill-sm"><Printer size={14}/> Save as PDF</button>
      </div>
    </div>
  );
}

function TestResultCard({testKey,test}) {
  if(!test)return null;
  const warnState=test.status==="no_response"||test.status==="timeout"||test.status==="skipped";
  const sc=test.status==="pass"?"var(--au-success)":warnState?"var(--au-warn)":"var(--au-error)";
  const led=sc;
  const sl=test.status==="pass"?"PASS":test.status==="no_response"?"NO RESPONSE":test.status==="timeout"?"TIMEOUT":test.status==="skipped"?"SKIPPED":"FAIL";
  return (
    <div style={{border:"1px solid var(--au-hair)",borderLeft:`4px solid ${sc}`,borderRadius:"var(--au-r-input)",padding:"14px 18px",marginBottom:10,background:"var(--au-glass)"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{display:"inline-flex",color:"var(--au-accent-ink)"}}><Ico icon={TEST_ICONS[testKey]||SettingsIcon} size={20}/></span>
          <span style={{fontSize:14,fontWeight:700,color:"var(--au-text)"}}>{TEST_LABELS[testKey]||testKey}</span>
        </div>
        <span className="au-chip au-chip-status" style={{color:sc}}><span className="au-led" style={{background:led,boxShadow:`0 0 6px ${led}`}}/> {sl}</span>
      </div>
      <div className="au-mono" style={{marginTop:8,fontSize:12,color:"var(--au-text-2)",fontFamily:"var(--au-font-mono)",lineHeight:1.6}}>
        {test.ip&&<div>IP: {test.ip}</div>}
        {test.time_ms!=null&&<div>DNS Time: {test.time_ms} ms</div>}
        {test.latency_ms!=null&&<div>Latency: {test.latency_ms} ms</div>}
        {test.avg_ms!=null&&<div>Avg Ping: {test.avg_ms} ms</div>}
        {test.cert_valid!=null&&<div>Cert: {test.cert_valid?"✓ Valid":"✗ Invalid"}</div>}
        {test.expiry&&<div>Expires: {new Date(test.expiry).toLocaleString()}</div>}
        {test.issuer&&<div style={{fontSize:11,color:"var(--au-muted)"}}>Issuer: {test.issuer.slice(0,80)}</div>}
        {test.response&&<div style={{marginTop:6,padding:8,background:"var(--au-glass-strong)",borderRadius:4,fontSize:11,whiteSpace:"pre-wrap",maxHeight:120,overflow:"auto"}}>{test.response.slice(0,300)}</div>}
        {/* Advanced (srv / quality / register / invite) */}
        {test.mos!=null&&<div style={{fontSize:15,fontWeight:800,color:mosColor(test.mos),marginTop:4}}>MOS {test.mos} — {test.quality} <span style={{fontSize:11,fontWeight:400,color:"var(--au-muted)"}}>(R-factor {test.r_factor})</span></div>}
        {test.avg_rtt_ms!=null&&<div>RTT {test.avg_rtt_ms} ms · Jitter {test.jitter_ms} ms · Loss {test.packet_loss_pct}% ({test.received}/{test.packets} rcvd)</div>}
        {test.code!=null&&<div>SIP <b style={{color:"var(--au-text)"}}>{test.code}</b> — {test.interpretation||test.reason}</div>}
        {test.authenticated!=null&&<div>Auth: {test.authenticated?"✓ registered":"✗ rejected"}</div>}
        {Array.isArray(test.codecs_offered_by_server)&&test.codecs_offered_by_server.length>0&&<div>Codecs: {test.codecs_offered_by_server.join(", ")}</div>}
        {test.media_port&&<div>Media port: {test.media_port}</div>}
        {test.nat&&<div>NAT: {test.nat.behind_nat?"⚠ behind NAT":"direct (no NAT)"}{test.nat.observed_public_ip?` — seen as ${test.nat.observed_public_ip}`:""}</div>}
        {test.records&&<div style={{marginTop:4}}>{["udp","tcp","tls"].map(k=>(test.records[k]||[]).map((r,i)=><div key={k+i} style={{fontSize:11,color:"var(--au-muted)"}}>_sip._{k}: {r.target}:{r.port} (pri {r.priority})</div>))}</div>}
        {test.error&&<div style={{color:"var(--au-error)"}}>Error: {test.error}</div>}
        {test.note&&<div style={{color:"var(--au-warn)",fontStyle:"italic"}}>{test.note}</div>}
      </div>
    </div>
  );
}

function HistoryTab({history,viewHistoryTest}) {
  if(history.length===0)return <div className="au-glass" style={{...S.card,padding:60,textAlign:"center"}}><div style={{display:"flex",justifyContent:"center",marginBottom:14,color:"var(--au-muted)"}}><FileText size={48}/></div><div style={{fontSize:16,color:"var(--au-text-2)"}}>No tests yet. Run your first diagnostic.</div></div>;
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(history,null,2)],{type:"application/json"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download=`getkhojo-sip-history-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url); toast.success("All history exported");
  };
  const exportCSV = () => {
    const headers = ["Server","Port","Score","DNS","TCP","UDP","TLS","ICMP","Date"];
    const rows = history.map(t => [t.server,t.port,t.overall_score,t.dns_status||"",t.tcp_status||"",t.udp_status||"",t.tls_status||"",t.icmp_status||"",new Date(t.created_at).toISOString()].join(","));
    const csv = [headers.join(","),...rows].join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download=`getkhojo-sip-history-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success("CSV exported");
  };
  return (
    <div className="au-glass" style={S.card}>
      <div style={{...S.cardHead,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <h2 style={S.cardH2}><FileText size={18}/> Past Tests ({history.length})</h2>
        <div style={{display:"flex",gap:8}}>
          <button onClick={exportCSV} className="au-pill au-pill-primary au-pill-sm"><BarChart3 size={14}/> Export CSV</button>
          <button onClick={exportJSON} className="au-pill au-pill-primary au-pill-sm"><Save size={14}/> Export All JSON</button>
        </div>
      </div>
      <div>
        <div style={{...S.tableRow,...S.tableHead}}><span>Server</span><span>Port</span><span>Score</span><span>DNS</span><span>TCP</span><span>UDP</span><span>TLS</span><span>Date</span><span></span></div>
        {history.map(t=>(
          <div key={t.id} style={S.tableRow}>
            <span className="au-mono" style={{fontFamily:"var(--au-font-mono)",fontSize:13,overflow:"hidden",textOverflow:"ellipsis"}}>{t.server}</span>
            <span>{t.port}</span>
            <span style={{fontWeight:700,color:t.overall_score>=70?"var(--au-success)":t.overall_score>=40?"var(--au-warn)":"var(--au-error)"}}>{t.overall_score}</span>
            <StatusDot s={t.dns_status}/><StatusDot s={t.tcp_status}/><StatusDot s={t.udp_status}/><StatusDot s={t.tls_status}/>
            <span style={{fontSize:11,color:"var(--au-muted)"}}>{new Date(t.created_at).toLocaleDateString()}</span>
            <button onClick={()=>viewHistoryTest(t.id)} className="au-pill au-pill-glass au-pill-sm">View</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusDot({s}){const c={pass:"var(--au-success)",fail:"var(--au-error)",no_response:"var(--au-warn)",timeout:"var(--au-warn)",error:"var(--au-error)"};const col=c[s]||"var(--au-muted)";return <span style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:col}}/>;}

function MultiAuthTab() {
  const [method,setMethod]=useState("ip");
  const methods=[
    {k:"ip",icon:Globe,name:"IP Whitelisting",desc:"Source IP-based authentication. No password."},
    {k:"digest",icon:KeyRound,name:"Digest Authentication",desc:"Standard SIP digest (username + password challenge)."},
    {k:"oauth",icon:UserCheck,name:"OAuth / JWT",desc:"Modern token-based auth for cloud SIP providers."},
    {k:"apikey",icon:Key,name:"API Key (Cloud)",desc:"REST API key — for cloud SIP providers."}
  ];
  const testAuth=()=>toast(`${methods.find(m=>m.k===method).name} testing coming soon`,{icon:"⏳"});
  return (
    <>
      <div className="au-glass" style={S.card}>
        <div style={S.cardHead}><h2 style={S.cardH2}><ShieldCheck size={18}/> Choose Authentication Method</h2></div>
        <div style={{padding:"24px 28px",display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:12}}>
          {methods.map(m=>(
            <button key={m.k} onClick={()=>setMethod(m.k)} style={{textAlign:"left",padding:"16px 18px",borderRadius:"var(--au-r-sm)",border:`2px solid ${method===m.k?"var(--au-accent)":"var(--au-hair)"}`,background:method===m.k?"var(--au-glass-strong)":"var(--au-glass)",cursor:"pointer"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <span style={{display:"inline-flex",color:method===m.k?"var(--au-accent-ink)":"var(--au-text-2)"}}><Ico icon={m.icon} size={24}/></span>
                <span style={{fontSize:14,fontWeight:700,color:method===m.k?"var(--au-accent-ink)":"var(--au-text)"}}>{m.name}</span>
              </div>
              <div style={{fontSize:12,color:"var(--au-text-2)",lineHeight:1.5}}>{m.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="au-glass" style={{...S.card,marginTop:18}}>
        <div style={S.cardHead}><h2 style={S.cardH2}><FlaskConical size={18}/> {methods.find(m=>m.k===method).name} — Test Form</h2></div>
        <div style={{padding:"24px 28px"}}>
          {method==="ip"&&<AuthForm fields={[{l:"SIP Server",ph:"your-pbx.example.com"},{l:"Port",ph:"5060"},{l:"Source IP (whitelisted)",ph:"203.0.113.5"}]}/>}
          {method==="digest"&&<AuthForm fields={[{l:"SIP Server",ph:"your-pbx.example.com"},{l:"Port",ph:"5060"},{l:"Username",ph:"100"},{l:"Password",ph:"••••••••",type:"password"},{l:"Realm (optional)",ph:"auto-detect"}]}/>}
          {method==="oauth"&&<AuthForm fields={[{l:"OAuth Provider URL",ph:"https://provider.example/oauth/token"},{l:"Client ID",ph:"client_abc123"},{l:"Client Secret",ph:"••••••••",type:"password"},{l:"Scope",ph:"sip.read sip.write"}]}/>}
          {method==="apikey"&&<AuthForm fields={[{l:"API Endpoint",ph:"https://provider.example/v1"},{l:"API Key",ph:"sk_live_••••",type:"password"},{l:"SIP Domain",ph:"your-domain.example"}]}/>}
          <button onClick={testAuth} className="au-pill au-pill-primary" style={{width:"100%",justifyContent:"center",padding:16,fontSize:15,marginTop:18}}><Play size={18}/> Run {methods.find(m=>m.k===method).name} Test</button>
        </div>
      </div>
    </>
  );
}

function AuthForm({fields}) {
  const [vals,setVals]=useState({});
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:14}}>
      {fields.map((f,i)=>(
        <div key={i}>
          <label style={S.label}>{f.l.toUpperCase()}</label>
          <input type={f.type||"text"} value={vals[f.l]||""} onChange={e=>setVals({...vals,[f.l]:e.target.value})} placeholder={f.ph} className="au-input"/>
        </div>
      ))}
    </div>
  );
}

function DialplanTab({dialplan,setDialplan,analyzeDialplan,dialplanResult,setDialplanResult}) {
  const examples={
    Asterisk:`[default]\nexten => 1000,1,Answer()\n same => n,Playback(welcome)\n same => n,Dial(SIP/100,30)\n same => n,Hangup()\n\n[internal]\nexten => _2XX,1,Dial(SIP/\${EXTEN},20)\n same => n,Voicemail(\${EXTEN}@default)`,
    FreeSWITCH:`<extension name="welcome">\n  <condition field="destination_number" expression="^1000$">\n    <action application="answer"/>\n    <action application="playback" data="welcome.wav"/>\n    <action application="bridge" data="user/100"/>\n  </condition>\n</extension>`,
    Kamailio:`route[REQINIT] {\n  if (!mf_process_maxfwd_header("10")) {\n    sl_send_reply("483","Too Many Hops");\n    exit;\n  }\n  if (is_method("INVITE")) {\n    route(RELAY);\n  }\n}\n\nroute[RELAY] {\n  if (!t_relay()) {\n    sl_reply_error();\n  }\n}`
  };
  const loadEx=(t)=>{setDialplan(examples[t]);setDialplanResult(null);toast.success(`${t} example loaded`);};
  return (
    <>
      <div className="au-glass" style={S.card}>
        <div style={{...S.cardHead,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
          <h2 style={S.cardH2}><ScrollText size={18}/> Dialplan Editor & Analyzer</h2>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {Object.keys(examples).map(t=><button key={t} onClick={()=>loadEx(t)} className="au-pill au-pill-glass au-pill-sm">Load {t}</button>)}
          </div>
        </div>
        <div style={{padding:"20px 28px"}}>
          <div style={{...S.miniLabel,marginBottom:6}}>YOUR DIALPLAN (Asterisk / FreeSWITCH / Kamailio)</div>
          <textarea value={dialplan} onChange={e=>setDialplan(e.target.value)} placeholder="Paste your dialplan here..." className="au-input au-mono" style={{fontFamily:"var(--au-font-mono)",fontSize:13,minHeight:280,resize:"vertical",whiteSpace:"pre",overflowWrap:"normal",overflowX:"auto"}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:12,gap:10,flexWrap:"wrap"}}>
            <div style={{fontSize:11,color:"var(--au-muted)"}}>{dialplan.split("\n").length} lines · {dialplan.length} chars</div>
            <button onClick={analyzeDialplan} className="au-pill au-pill-primary"><Play size={18}/> Analyze Dialplan</button>
          </div>
        </div>
      </div>
      {dialplanResult&&(
        <div className="au-glass" style={{...S.card,marginTop:18}}>
          <div style={S.cardHead}><h2 style={S.cardH2}><BarChart3 size={18}/> Parse Results — {dialplanResult.type}</h2></div>
          <div style={{padding:"24px 28px"}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:18}}>
              <ResultStat label="Type" value={dialplanResult.type}/>
              <ResultStat label="Lines" value={dialplanResult.lines}/>
              <ResultStat label="Extensions" value={dialplanResult.extensions.length}/>
              <ResultStat label="Contexts" value={dialplanResult.contexts.length}/>
              <ResultStat label="Routes" value={dialplanResult.routes.length}/>
            </div>
            {dialplanResult.extensions.length>0&&<ParsedList title="Extensions Detected" items={dialplanResult.extensions}/>}
            {dialplanResult.contexts.length>0&&<ParsedList title="Contexts" items={dialplanResult.contexts}/>}
            {dialplanResult.routes.length>0&&<ParsedList title="Routes" items={dialplanResult.routes}/>}
            {dialplanResult.actions.length>0&&<ParsedList title="Actions / Applications Used" items={dialplanResult.actions}/>}
          </div>
        </div>
      )}
    </>
  );
}

function ResultStat({label,value}){return <div style={{background:"var(--au-glass-strong)",padding:"12px 16px",borderRadius:"var(--au-r-input)",border:"1px solid var(--au-hair)"}}><div style={{fontSize:10,color:"var(--au-muted)",letterSpacing:1.5,fontWeight:700,marginBottom:4}}>{label.toUpperCase()}</div><div style={{fontSize:20,fontWeight:800,color:"var(--au-text)"}}>{value}</div></div>;}
function ParsedList({title,items}){return <div style={{marginBottom:14}}><div style={{...S.miniLabel,marginBottom:8}}>{title.toUpperCase()} ({items.length})</div><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{items.map((x,i)=><span key={i} className="au-mono" style={{padding:"4px 10px",background:"var(--au-glass-strong)",border:"1px solid var(--au-hair)",borderRadius:"var(--au-r-pill)",fontSize:12,color:"var(--au-text-2)",fontFamily:"var(--au-font-mono)"}}>{x}</span>)}</div></div>;}

function FlowTab() {
  const steps=[
    {step:1,msg:"REGISTER",dir:"→",from:"Client",to:"SIP Server",desc:"Client registers with server"},
    {step:2,msg:"401 Unauthorized",dir:"←",from:"SIP Server",to:"Client",desc:"Server requests auth"},
    {step:3,msg:"REGISTER (with auth)",dir:"→",from:"Client",to:"SIP Server",desc:"Client sends credentials"},
    {step:4,msg:"200 OK",dir:"←",from:"SIP Server",to:"Client",desc:"Registration successful"},
    {step:5,msg:"INVITE",dir:"→",from:"Caller",to:"SIP Server",desc:"Call initiated"},
    {step:6,msg:"100 Trying",dir:"←",from:"SIP Server",to:"Caller",desc:"Processing call"},
    {step:7,msg:"180 Ringing",dir:"←",from:"SIP Server",to:"Caller",desc:"Callee phone ringing"},
    {step:8,msg:"200 OK",dir:"←",from:"SIP Server",to:"Caller",desc:"Call answered"},
    {step:9,msg:"ACK",dir:"→",from:"Caller",to:"SIP Server",desc:"Confirm — call established"},
    {step:10,msg:"RTP Stream",dir:"⇄",from:"Caller",to:"Callee",desc:"Audio flowing"},
    {step:11,msg:"BYE",dir:"→",from:"Caller",to:"SIP Server",desc:"Call hangup"},
    {step:12,msg:"200 OK",dir:"←",from:"SIP Server",to:"Caller",desc:"Call ended"}
  ];
  return (
    <div className="au-glass" style={S.card}>
      <div style={S.cardHead}><h2 style={S.cardH2}><Shuffle size={18}/> Standard SIP Call Flow — Reference</h2></div>
      <div style={{padding:"24px 28px"}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:18,padding:"12px 20px",background:"var(--au-glass-strong)",borderRadius:"var(--au-r-input)"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,fontSize:14,fontWeight:700,color:"var(--au-accent-ink)"}}><Phone size={16}/> Client / Caller</div>
          <div style={{display:"flex",alignItems:"center",gap:6,fontSize:14,fontWeight:700,color:"var(--au-info)"}}><Server size={16}/> SIP Server</div>
          <div style={{display:"flex",alignItems:"center",gap:6,fontSize:14,fontWeight:700,color:"var(--au-info)"}}><Phone size={16}/> Callee</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {steps.map(s=>(
            <div key={s.step} style={{display:"grid",gridTemplateColumns:"40px 1fr",gap:14,padding:"10px 14px",background:"var(--au-glass)",borderLeft:"3px solid var(--au-accent)",borderRadius:6,alignItems:"center"}}>
              <div className="au-mono" style={{fontSize:12,fontWeight:700,color:"var(--au-muted)",fontFamily:"var(--au-font-mono)"}}>#{s.step}</div>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,flexWrap:"wrap"}}>
                  <span style={{fontWeight:700,color:"var(--au-text)"}}>{s.from}</span>
                  <span style={{fontSize:18,color:"var(--au-accent-ink)"}}>{s.dir}</span>
                  <span style={{fontWeight:700,color:"var(--au-text)"}}>{s.to}</span>
                  <span className="au-mono" style={{padding:"2px 8px",background:"var(--au-accent)",color:"var(--au-btn-text)",borderRadius:4,fontSize:11,fontFamily:"var(--au-font-mono)",fontWeight:700}}>{s.msg}</span>
                </div>
                <div style={{fontSize:11,color:"var(--au-muted)",marginTop:3}}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CodecsTab() {
  const codecs=[
    {name:"G.711 (PCMU/PCMA)",bw:"64 kbps",quality:"Excellent",use:"Standard VoIP — most compatible. Default for many providers."},
    {name:"G.722",bw:"64 kbps",quality:"HD",use:"Wideband audio — better than G.711 for same bandwidth."},
    {name:"G.729",bw:"8 kbps",quality:"Good",use:"Low bandwidth — popular in enterprise/mobile (licensed)."},
    {name:"Opus",bw:"6-510 kbps",quality:"Excellent",use:"Modern, adaptive — recommended for WebRTC and modern apps."},
    {name:"iLBC",bw:"15.2 kbps",quality:"Good",use:"Narrowband, packet-loss resilient. Internet streaming."},
    {name:"GSM",bw:"13 kbps",quality:"Fair",use:"Legacy mobile — old systems."},
    {name:"Speex",bw:"2-44 kbps",quality:"Good",use:"Open-source, variable bitrate. Used in older VoIP apps."},
    {name:"AMR-WB",bw:"6.6-23.85 kbps",quality:"HD",use:"Mobile HD voice — 3GPP standard, used in VoLTE."}
  ];
  return (
    <div className="au-glass" style={S.card}>
      <div style={S.cardHead}><h2 style={S.cardH2}><Music size={18}/> Common VoIP Codecs</h2></div>
      <div>
        {codecs.map((c,i)=>(
          <div key={i} style={{padding:"16px 28px",borderBottom:i<codecs.length-1?"1px solid var(--au-hair)":"none"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6,gap:10,flexWrap:"wrap"}}>
              <div style={{fontSize:15,fontWeight:700,color:"var(--au-text)"}}>{c.name}</div>
              <div style={{display:"flex",gap:8}}>
                <span style={S.tag}>{c.bw}</span>
                <span style={{...S.tag,background:"var(--au-glass-strong)",color:"var(--au-accent-ink)"}}>{c.quality}</span>
              </div>
            </div>
            <div style={{fontSize:13,color:"var(--au-text-2)",lineHeight:1.6}}>{c.use}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function APICliTab() {
  return (
    <>
      <div className="au-glass" style={{...S.card,border:"1px solid var(--au-warn)"}}>
        <div style={{padding:"30px 32px"}}>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:14,flexWrap:"wrap"}}>
            <span style={{display:"inline-flex",color:"var(--au-accent-ink)"}}><MonitorCog size={36}/></span>
            <div>
              <div style={{fontSize:22,fontWeight:800,color:"var(--au-text)"}}>API & CLI Access</div>
              <div style={{fontSize:14,color:"var(--au-text-2)",marginTop:4}}>For servers that can't access the web UI</div>
            </div>
            <span className="au-chip au-chip-status" style={{marginLeft:"auto",color:"var(--au-warn)"}}>PAID · COMING SOON</span>
          </div>
          <div style={{fontSize:14,color:"var(--au-text-2)",lineHeight:1.7,marginBottom:18}}>
            Run Aegis SIP diagnostics directly from your SIP server's terminal via menu-driven CLI. Perfect for hardened production servers without browser access.
          </div>
        </div>
      </div>
      <div className="au-glass" style={{...S.card,marginTop:18}}>
        <div style={S.cardHead}><h2 style={S.cardH2}><Terminal size={18}/> Example: Menu-Driven CLI</h2></div>
        <div style={{padding:"24px 28px"}}>
          <pre className="au-mono" style={{background:"var(--au-bg-artboard, #1A1A1A)",color:"var(--au-success)",padding:"20px",borderRadius:"var(--au-r-input)",fontFamily:"var(--au-font-mono)",fontSize:13,overflow:"auto",lineHeight:1.6,margin:0}}>{`$ curl -sL https://api.getkhojo.com/cli | bash
$ getkhojo-sip
╔══════════════════════════════════════╗
║   GETKHOJO SIP TESTER — CLI v1.0       ║
╠══════════════════════════════════════╣
║  1. Run quick diagnostic            ║
║  2. Test SIP REGISTER auth          ║
║  3. Test RTP echo                   ║
║  4. Test codec advertisement        ║
║  5. Analyze dialplan file           ║
║  6. Trace call (forensics)          ║
║  7. View test history               ║
║  8. Export PCAP                     ║
║  0. Exit                            ║
╚══════════════════════════════════════╝
Select option: 1
Enter SIP server: your-pbx.example.com
Enter port [5060]:
Running diagnostics...
✓ DNS resolved: 203.0.113.45 (12 ms)
✓ TCP connect: pass (45 ms)
✓ SIP OPTIONS: pass — Asterisk 18.2
✗ TLS 5061: connection refused
✓ ICMP: avg 18ms
Overall: 80/100`}</pre>
        </div>
      </div>
      <div className="au-glass" style={{...S.card,marginTop:18}}>
        <div style={S.cardHead}><h2 style={S.cardH2}><ShieldCheck size={18}/> Direct REST API</h2></div>
        <div style={{padding:"24px 28px"}}>
          <pre className="au-mono" style={{background:"var(--au-bg-artboard, #1A1A1A)",color:"var(--au-info)",padding:"20px",borderRadius:"var(--au-r-input)",fontFamily:"var(--au-font-mono)",fontSize:13,overflow:"auto",lineHeight:1.6,margin:0}}>{`# Run diagnostic via API
curl -X POST https://api.getkhojo.com/v1/sip/test \\
  -H "X-API-Key: ak_live_xxxxxxxxxxxxx" \\
  -d '{"server":"your-sip.example.com","port":5060}'

# Returns JSON with all test results, score, recommendations`}</pre>
        </div>
      </div>
      <div className="au-glass" style={{...S.card,marginTop:18}}>
        <div style={{padding:"20px 28px",fontSize:13,color:"var(--au-text-2)",lineHeight:1.7}}>
          <strong style={{display:"inline-flex",alignItems:"center",gap:6,color:"var(--au-accent-ink)"}}><Zap size={14}/> Why Paid?</strong> Daily limits per API key prevent misuse. Indigenous infrastructure, secure architecture. Subscribe = instant key + early access when ready.
        </div>
      </div>
    </>
  );
}

function ForensicsTab() {
  return (
    <>
      <div className="au-glass" style={{...S.card,border:"1px solid var(--au-error)"}}>
        <div style={{padding:"30px 32px"}}>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:14,flexWrap:"wrap"}}>
            <span style={{display:"inline-flex",color:"var(--au-error)"}}><Shield size={36}/></span>
            <div>
              <div style={{fontSize:22,fontWeight:800,color:"var(--au-text)"}}>Call Forensics</div>
              <div style={{fontSize:14,color:"var(--au-text-2)",marginTop:4}}>If your SIP server got hacked — trace the source</div>
            </div>
            <span className="au-chip au-chip-status" style={{marginLeft:"auto",color:"var(--au-error)"}}>PAID · COMING SOON</span>
          </div>
          <div style={{fontSize:14,color:"var(--au-text-2)",lineHeight:1.7}}>
            VoIP fraud, call hijacking, toll fraud — trace the source. Upload your CDR / SIP logs / PCAP, get attack vector + origin IP + call patterns + ISP/ASN identification.
          </div>
        </div>
      </div>
      <div className="au-glass" style={{...S.card,marginTop:18}}>
        <div style={S.cardHead}><h2 style={S.cardH2}><CircleHelp size={18}/> What Call Forensics Does</h2></div>
        <div style={{padding:"20px 28px",fontSize:13,color:"var(--au-text-2)",lineHeight:1.8}}>
          <ul style={{margin:0,paddingLeft:20}}>
            <li><strong>Upload CDR/log</strong> — Asterisk CDR, FreeSWITCH log, SIP capture file</li>
            <li><strong>Pattern detection</strong> — sudden spike, unusual destinations, brute-force REGISTER</li>
            <li><strong>Source IP analysis</strong> — geographic + ISP/ASN identification, VPN/proxy detection</li>
            <li><strong>Attack vector</strong> — SIP brute force? Stolen credentials? Compromised PBX config?</li>
            <li><strong>Cost reconstruction</strong> — total damage in INR + duration of compromise</li>
            <li><strong>Hardening recommendations</strong> — specific fixes for Asterisk/FreeSWITCH/Kamailio</li>
            <li><strong>Report PDF</strong> — share with telecom provider for dispute / refund claims</li>
          </ul>
        </div>
      </div>
    </>
  );
}

function SettingsTab({settings,setSettings,enabled,setEnabled,savedServers,setSavedServers}) {
  const updateS=(k,v)=>setSettings({...settings,[k]:v});
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div className="au-glass" style={S.card}>
        <div style={S.cardHead}><h2 style={S.cardH2}><SettingsIcon size={18}/> Default Test Preferences</h2></div>
        <div style={{padding:"20px 28px"}}>
          <div style={{fontSize:13,color:"var(--au-text-2)",marginBottom:14}}>Tests enabled by default on the SIP Test tab.</div>
          {Object.entries(TEST_LABELS).map(([k,label])=>(
            <label key={k} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:"1px solid var(--au-hair)"}}>
              <input type="checkbox" checked={enabled[k]} onChange={e=>setEnabled({...enabled,[k]:e.target.checked})} style={{accentColor:"var(--au-accent)",width:18,height:18}}/>
              <span style={{display:"inline-flex",alignItems:"center",gap:8,fontSize:14,color:"var(--au-text)",fontWeight:600}}><Ico icon={TEST_ICONS[k]} size={16}/> {label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="au-glass" style={S.card}>
        <div style={S.cardHead}><h2 style={S.cardH2}><Wrench size={18}/> Connection Defaults</h2></div>
        <div style={{padding:"20px 28px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
          <div>
            <label style={S.label}>DEFAULT PORT</label>
            <input value={settings.defaultPort} onChange={e=>updateS("defaultPort",e.target.value.replace(/\D/g,""))} className="au-input"/>
            <div style={S.hint}>Used when SIP Test tab loads</div>
          </div>
          <div>
            <label style={S.label}>TIMEOUT (seconds)</label>
            <input type="number" min="1" max="60" value={settings.timeoutSec} onChange={e=>updateS("timeoutSec",parseInt(e.target.value)||5)} className="au-input"/>
            <div style={S.hint}>How long to wait per test</div>
          </div>
        </div>
      </div>

      <div className="au-glass" style={S.card}>
        <div style={S.cardHead}><h2 style={S.cardH2}><Bell size={18}/> Alerts & Notifications</h2></div>
        <div style={{padding:"20px 28px"}}>
          <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:"1px solid var(--au-hair)",cursor:"pointer"}}>
            <input type="checkbox" checked={settings.autoSaveResults} onChange={e=>updateS("autoSaveResults",e.target.checked)} style={{accentColor:"var(--au-accent)",width:18,height:18}}/>
            <div>
              <div style={{fontSize:14,color:"var(--au-text)",fontWeight:600}}>Auto-save test results to history</div>
              <div style={{fontSize:12,color:"var(--au-muted)"}}>Recommended — keeps record of all tests</div>
            </div>
          </label>
          <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:"1px solid var(--au-hair)",cursor:"pointer"}}>
            <input type="checkbox" checked={settings.emailAlerts} onChange={e=>updateS("emailAlerts",e.target.checked)} style={{accentColor:"var(--au-accent)",width:18,height:18}}/>
            <div>
              <div style={{fontSize:14,color:"var(--au-text)",fontWeight:600}}>Email me when tests complete</div>
              <div style={{fontSize:12,color:"var(--au-muted)"}}>Get diagnostic results via email (coming soon)</div>
            </div>
          </label>
          <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",cursor:"pointer"}}>
            <input type="checkbox" checked={settings.emailOnFail} onChange={e=>updateS("emailOnFail",e.target.checked)} style={{accentColor:"var(--au-accent)",width:18,height:18}}/>
            <div>
              <div style={{fontSize:14,color:"var(--au-text)",fontWeight:600}}>Email only on failures</div>
              <div style={{fontSize:12,color:"var(--au-muted)"}}>Quieter — only when score &lt; 70 (coming soon)</div>
            </div>
          </label>
        </div>
      </div>

      <div className="au-glass" style={S.card}>
        <div style={{...S.cardHead,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <h2 style={S.cardH2}><Pin size={18}/> Saved Servers ({savedServers.length})</h2>
          {savedServers.length>0&&<button onClick={()=>setSavedServers([])} className="au-pill au-pill-glass au-pill-sm">Clear All</button>}
        </div>
        <div style={{padding:"20px 28px"}}>
          {savedServers.length===0?(
            <div style={{fontSize:13,color:"var(--au-text-2)"}}>No saved servers yet. Save servers from the SIP Test tab to quickly re-test later.</div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {savedServers.map((s,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",background:"var(--au-glass)",borderRadius:6}}>
                  <div>
                    <div className="au-mono" style={{fontSize:13,fontWeight:600,color:"var(--au-text)",fontFamily:"var(--au-font-mono)"}}>{s.server}:{s.port}</div>
                    <div style={{fontSize:11,color:"var(--au-muted)"}}>{s.type||"Generic SIP"} · saved {new Date(s.saved).toLocaleDateString()}</div>
                  </div>
                  <button onClick={()=>setSavedServers(savedServers.filter((_,idx)=>idx!==i))} className="au-pill au-pill-glass au-pill-sm" style={{color:"var(--au-error)"}}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DocsTab() {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <DocSection icon={Target} title="What Is Aegis SIP Tester?" body={<>
        <p><strong>Indigenous Indian VoIP diagnostics platform</strong> by <strong>GetKhojo</strong>. Verifies SIP infrastructure (Asterisk, FreeSWITCH, Kamailio, OpenSIPS, custom PBXes) — network reachability, port availability, TLS validity, SIP protocol response.</p>
        <p><strong>For</strong>: Indian VoIP engineers, ISPs, telecom operators, BPOs running call centers, enterprises with PBX deployments.</p>
      </>}/>
      <DocSection icon={MapPin} title="Which IP Should I Test?" body={<>
        <p>SIP infrastructure has multiple IPs. Test what you control:</p>
        <ul>
          <li><strong>Public/Signaling IP</strong> — Your SIP server's public IP (e.g. your-pbx.example.com). <strong>Most common test.</strong></li>
          <li><strong>RTP/Media IP</strong> — Audio stream IP. Sometimes different from signaling.</li>
          <li><strong>Internal IP (192.168.x.x / 10.x.x.x)</strong> — Only reachable inside network. Won't work from this tool.</li>
          <li><strong>Trunk Provider IP</strong> — Your upstream SIP trunk (Tata, BT, Plivo, etc.).</li>
        </ul>
        <div style={S.tip}><span style={{display:"inline-flex",verticalAlign:"middle",marginRight:6,color:"var(--au-accent-ink)"}}><Lightbulb size={14}/></span><strong>Your home/office IP</strong> and <strong>SIP server IP</strong> are USUALLY different. Test your <strong>server's</strong> address.</div>
      </>}/>
      <DocSection icon={Server} title="Server Types Supported" body={<ul>
        <li><strong>Asterisk</strong> — chan_sip on 5060 (legacy) or PJSIP on 5060/5080 (modern)</li>
        <li><strong>FreeSWITCH</strong> — Internal profile (5060) for registered users, External (5080) for public trunks</li>
        <li><strong>Kamailio / OpenSIPS</strong> — SIP proxies on 5060</li>
        <li><strong>3CX, FusionPBX, FreePBX, Issabel</strong> — All standard SIP</li>
        <li><strong>SIP over TLS</strong> — Port 5061, encrypted signaling</li>
        <li><strong>WebSocket (WSS)</strong> — Port 443, WebRTC clients</li>
      </ul>}/>
      <DocSection icon={Plug} title="Common SIP Ports" body={<>
        <table style={S.docTable}>
          <thead><tr style={{background:"var(--au-glass-strong)"}}><th style={S.th}>Port</th><th style={S.th}>Protocol</th><th style={S.th}>Use Case</th></tr></thead>
          <tbody>
            <tr><td style={S.td}>5060</td><td style={S.td}>UDP/TCP</td><td style={S.td}>Standard SIP — most common</td></tr>
            <tr><td style={S.td}>5061</td><td style={S.td}>TLS</td><td style={S.td}>SIP over TLS — encrypted</td></tr>
            <tr><td style={S.td}>5070</td><td style={S.td}>UDP/TCP</td><td style={S.td}>Alternative SIP — B2BUA, proxy</td></tr>
            <tr><td style={S.td}>5080</td><td style={S.td}>UDP/TCP</td><td style={S.td}>FreeSWITCH External / Asterisk PJSIP</td></tr>
            <tr><td style={S.td}>443</td><td style={S.td}>TCP/WSS</td><td style={S.td}>SIP over WebSocket — WebRTC</td></tr>
          </tbody>
        </table>
      </>}/>
      <DocSection icon={FlaskConical} title="What Each Test Does" body={<ul>
        <li><strong>DNS Resolution</strong> — Converts hostname to IP. If fails: DNS broken or hostname doesn't exist.</li>
        <li><strong>TCP Connect</strong> — Establishes TCP socket. If fails: port closed/firewalled or server down.</li>
        <li><strong>SIP OPTIONS (UDP)</strong> — Sends real SIP OPTIONS packet. "No response" often = server filters UDP. "Pass" = server responding to SIP.</li>
        <li><strong>TLS Certificate</strong> — Validates SSL cert chain on TLS port. Checks expiry, issuer.</li>
        <li><strong>ICMP Ping</strong> — Basic network reachability + RTT.</li>
      </ul>}/>
      <DocSection icon={CircleHelp} title="FAQ" body={<>
        <p><strong>Q: Is this tool indigenous?</strong></p>
        <p>A: Yes — built in India by GetKhojo. Your data stays on Indian servers. No third-party sharing.</p>
        <p><strong>Q: Why does UDP SIP show "no response"?</strong></p>
        <p>A: Many SIP servers filter UDP OPTIONS from unknown sources. Security feature. Try TCP/TLS.</p>
        <p><strong>Q: Tests pass — will my calls work?</strong></p>
        <p>A: Network OK is necessary but not sufficient. Auth, codec negotiation, RTP — separate. Use Multi-Auth tab for auth testing.</p>
      </>}/>
    </div>
  );
}

function DocSection({icon,title,body}){return <div className="au-glass" style={S.card}><div style={S.cardHead}><h2 style={{display:"flex",alignItems:"center",gap:8,fontSize:17,fontWeight:700,color:"var(--au-text)",margin:0}}><Ico icon={icon} size={18}/> {title}</h2></div><div style={{padding:"20px 28px",fontSize:14,color:"var(--au-text-2)",lineHeight:1.7}}>{body}</div></div>;}

function NavItem({icon,label,active,onClick,badge,paid}){
  return <button onClick={onClick} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"11px 14px",background:active?"var(--au-glass-strong)":"transparent",color:active?"var(--au-accent-ink)":"var(--au-text-2)",border:"none",borderLeft:active?"3px solid var(--au-accent)":"3px solid transparent",fontSize:13,fontWeight:active?700:500,cursor:"pointer",textAlign:"left"}}>
    <span style={{display:"inline-flex"}}><Ico icon={icon} size={16}/></span><span style={{flex:1}}>{label}</span>
    {badge!=null&&badge>0&&<span style={{background:"var(--au-accent)",color:"var(--au-btn-text)",borderRadius:10,padding:"2px 7px",fontSize:10,fontWeight:700}}>{badge}</span>}
    {paid&&<span style={{background:"var(--au-warn)",color:"#fff",borderRadius:4,padding:"2px 6px",fontSize:9,fontWeight:700,letterSpacing:0.5}}>PAID</span>}
  </button>;
}
function StatCard({icon,label,value,hint}){return <div className="au-glass au-pad" style={{padding:"18px 20px"}}><div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,color:"var(--au-accent-ink)"}}><Ico icon={icon} size={18}/><span style={{fontSize:11,color:"var(--au-muted)",letterSpacing:1,fontWeight:600}}>{label}</span></div><div style={{fontSize:26,fontWeight:800,color:"var(--au-text)",marginBottom:4}}>{value}</div><div style={{fontSize:11,color:"var(--au-muted)"}}>{hint}</div></div>;}
function InfoCard({icon,title,desc}){return <div className="au-glass au-pad" style={{padding:"18px 20px"}}><div style={{marginBottom:10,color:"var(--au-accent-ink)"}}><Ico icon={icon} size={24}/></div><div style={{fontSize:14,fontWeight:700,color:"var(--au-text)",marginBottom:6}}>{title}</div><div style={{fontSize:12,color:"var(--au-text-2)",lineHeight:1.6}}>{desc}</div></div>;}

const S={
  sidebar:{width:240,padding:"24px 0",display:"flex",flexDirection:"column",position:"sticky",top:0,height:"100vh",borderRadius:0},
  brand:{padding:"0 22px 24px",borderBottom:"1px solid var(--au-hair)"},
  userBox:{padding:"16px 22px",borderTop:"1px solid var(--au-hair)",fontSize:12},
  headerRow:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28,flexWrap:"wrap",gap:12},
  card:{overflow:"hidden"},
  cardHead:{padding:"18px 28px",borderBottom:"1px solid var(--au-hair)"},
  cardH2:{display:"flex",alignItems:"center",gap:8,fontSize:18,fontWeight:700,color:"var(--au-text)",margin:0},
  label:{display:"block",fontSize:12,color:"var(--au-text)",fontWeight:600,marginBottom:8,letterSpacing:0.5},
  miniLabel:{fontSize:11,color:"var(--au-muted)",letterSpacing:1,marginBottom:8,fontWeight:600},
  hint:{fontSize:11,color:"var(--au-muted)",marginTop:6},
  tableRow:{display:"grid",gridTemplateColumns:"2fr 0.6fr 0.6fr 0.5fr 0.5fr 0.5fr 0.5fr 1fr 0.7fr",padding:"12px 28px",alignItems:"center",borderBottom:"1px solid var(--au-hair)",fontSize:13,gap:8,color:"var(--au-text)"},
  tableHead:{fontSize:10,color:"var(--au-muted)",letterSpacing:1.5,textTransform:"uppercase",fontWeight:700,background:"var(--au-glass)"},
  tag:{padding:"3px 8px",background:"var(--au-glass-strong)",color:"var(--au-text-2)",borderRadius:4,fontSize:11,fontWeight:600,whiteSpace:"nowrap"},
  tip:{marginTop:10,padding:"10px 14px",background:"var(--au-glass-strong)",border:"1px solid var(--au-hair)",borderRadius:"var(--au-r-input)",fontSize:13,color:"var(--au-text-2)"},
  docTable:{width:"100%",borderCollapse:"collapse",fontSize:13,marginTop:4},
  th:{padding:"10px 12px",textAlign:"left",fontSize:11,letterSpacing:1,color:"var(--au-text)",fontWeight:700,borderBottom:"1px solid var(--au-hair)"},
  td:{padding:"10px 12px",borderBottom:"1px solid var(--au-hair)",color:"var(--au-text-2)"}
};
